"use client";

import { ChevronDown, ImageIcon } from "lucide-react";
import { useCallback, useState } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTriggerWithoutIcon,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

import { VS_FONT_FEATURES } from "./constants";
import type { GalleryImage } from "./data";
import { LONDON_GALLERY_IMAGES, NY_GALLERY_IMAGES } from "./data";
import { VsLightbox } from "./lightbox";

interface VsGalleryProps {
  className?: string;
  /**
   * Skip the id/eyebrow/heading/description block — used when this photo
   * grid is embedded under another component's heading (e.g. folded into
   * the combined "Gallery" section, right below the clips reel strip)
   * rather than standing on its own.
   */
  hideHeading?: boolean;
}

/** Number of placeholder tiles shown while no real photos are wired up yet. */
const PLACEHOLDER_TILE_COUNT = 12;

interface GalleryCity {
  id: string;
  label: string;
  images: readonly GalleryImage[];
}

/**
 * "Gallery" — photos split into a London and a New York dropdown, each with
 * its own responsive grid, "See more" toggle, and lightbox. Driven by
 * `LONDON_GALLERY_IMAGES` and `NY_GALLERY_IMAGES` in `data.ts`. A city's
 * dropdown is only rendered once it has photos — when both are empty, the
 * section falls back to muted placeholder tiles so it still reads as
 * "photos coming".
 */
export function VsGallery({ className, hideHeading = false }: VsGalleryProps) {
  const cities: GalleryCity[] = [
    { id: "london", label: "London", images: LONDON_GALLERY_IMAGES },
    { id: "new-york", label: "New York", images: NY_GALLERY_IMAGES },
  ].filter((city) => city.images.length > 0);
  const hasPhotos = cities.length > 0;

  return (
    <div
      id={hideHeading ? undefined : "gallery"}
      style={{ fontFeatureSettings: VS_FONT_FEATURES }}
      className={cn(
        !hideHeading && "scroll-mt-[120px] py-[72px] max-md:py-12",
        className
      )}
    >
      {!hideHeading ? (
        <>
          <p className="mb-3 text-[12px] tracking-[0.0967em] text-[#95999d] uppercase">
            gallery
          </p>
          <h2 className="mb-3 text-[40px] leading-[1.2] font-normal tracking-[-1.6px] text-white max-md:text-[32px] max-md:tracking-[-1.28px]">
            Moments captured.
          </h2>
          <p className="mb-12 max-w-[640px] text-[14px] leading-[1.6] text-white/70 max-md:mb-9">
            {hasPhotos
              ? "Moments from the panels, the venues, and the founders who joined us in London and New York."
              : "Photos from the summits are on their way."}
          </p>
        </>
      ) : null}

      {hasPhotos ? (
        <Accordion
          multiple
          defaultValue={cities.map((city) => city.id)}
          className="w-full"
        >
          {cities.map((city) => (
            <AccordionItem
              key={city.id}
              value={city.id}
              className="border-b border-[#313335]"
            >
              <AccordionTriggerWithoutIcon
                className={cn(
                  "group py-5 text-left text-[16px] font-medium text-white hover:no-underline",
                  "rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0d]"
                )}
              >
                <span className="flex-1">
                  {city.label}{" "}
                  <span className="text-white/40">({city.images.length})</span>
                </span>
                <ChevronDown
                  aria-hidden
                  size={16}
                  strokeWidth={1.5}
                  className="size-4 shrink-0 transition-transform duration-200 group-data-open:rotate-180 motion-reduce:transition-none"
                />
              </AccordionTriggerWithoutIcon>
              <AccordionContent className="pb-8">
                <GalleryGrid images={city.images} cityLabel={city.label} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {/* TODO(visionaries): these placeholder tiles disappear automatically
              once LONDON_GALLERY_IMAGES or NY_GALLERY_IMAGES has real photos. */}
          {Array.from({ length: PLACEHOLDER_TILE_COUNT }).map((_, i) => (
            <div
              key={i}
              aria-hidden
              className="flex aspect-square items-center justify-center rounded-2xl border border-[#313335] bg-[#171717]"
            >
              <ImageIcon
                size={24}
                strokeWidth={1.5}
                className="text-white/20"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Photos shown before the "See more" toggle is expanded. */
const INITIAL_COUNT = 8;

interface GalleryGridProps {
  images: readonly GalleryImage[];
  /** Used in the lightbox's accessible title. */
  cityLabel: string;
}

/** One city's responsive photo grid, "See more" toggle, and lightbox with
 *  left/right navigation (arrow keys + on-screen controls) — scoped to
 *  just this city's photos. */
function GalleryGrid({ images, cityLabel }: GalleryGridProps) {
  const [expanded, setExpanded] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const count = images.length;

  const prev = useCallback(
    () => setLightboxIndex((i) => (i === null ? i : (i - 1 + count) % count)),
    [count]
  );
  const next = useCallback(
    () => setLightboxIndex((i) => (i === null ? i : (i + 1) % count)),
    [count]
  );

  const visible = expanded ? images : images.slice(0, INITIAL_COUNT);
  const active = lightboxIndex !== null ? images[lightboxIndex] : null;

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {visible.map((image, i) => (
          <button
            key={image.src}
            type="button"
            onClick={() => setLightboxIndex(i)}
            aria-label={`Open photo: ${image.alt}`}
            className={cn(
              "group relative aspect-square overflow-hidden rounded-2xl bg-[#171717]",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0d]"
            )}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={image.src}
              alt={image.alt}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105 motion-reduce:transition-none"
            />
          </button>
        ))}
      </div>

      {count > INITIAL_COUNT ? (
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className={cn(
              "inline-flex h-10 items-center rounded-full border border-[#313335] px-5 text-[14px] font-medium text-white",
              "transition-colors hover:bg-white/5 motion-reduce:transition-none",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0d]"
            )}
          >
            {expanded ? "See fewer" : `See more (${count - INITIAL_COUNT})`}
          </button>
        </div>
      ) : null}

      {/* Near-fullscreen lightbox (focus-trapped via the shared Dialog). */}
      <VsLightbox
        open={active !== null}
        onOpenChange={(o) => {
          if (!o) setLightboxIndex(null);
        }}
        title={`${cityLabel} photo viewer`}
        onPrev={prev}
        onNext={next}
      >
        {active ? (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={active.src}
              alt={active.alt}
              className="max-h-[88vh] max-w-[92vw] rounded-lg object-contain"
            />
            <p className="absolute bottom-5 left-1/2 -translate-x-1/2 text-[13px] text-white/70">
              {(lightboxIndex ?? 0) + 1} / {count}
            </p>
          </>
        ) : null}
      </VsLightbox>
    </div>
  );
}
